import React, { ChangeEvent, ReactChild } from "react";
import { Switch, Route, RouteComponentProps } from "react-router-dom";
import BrainTreeDropin, { Dropin } from "braintree-web-drop-in";

import { V1 } from "../../Api";

import { COPY_YEAR } from "../../Const";

import HCWHLogo from "../../images/hcwh_logo_white_small_notext.png";
import TWCLogo from "../../images/twc_white.png";

import "../../css/form.css";
import "../../css/list.css";
import "../../css/checkbox.css";

type PayProgress = "amount" | "method";

interface IPaymentRootState {
    globalLoading: boolean | string;
    isBillNotFound: boolean;
    isSubmitting: boolean | string;
    isPaymentLoading: boolean | string;
    isPaymentPending: boolean | string;

    paymentAgree: boolean;
    isPaymentRequestable: boolean;
    statement: any;
    deviceData?: string;

    showIOSWarning: boolean;
    hasCopied: boolean;

    selectedPayAmount: string;

    progress: PayProgress;
}

class PaymentRoot extends React.Component<RouteComponentProps<any>, IPaymentRootState> {
    private dropin?: Dropin;

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        this.state = {
            globalLoading: true,
            isBillNotFound: false,
            isSubmitting: false,
            isPaymentLoading: true,
            isPaymentPending: false,

            paymentAgree: false,
            isPaymentRequestable: false,
            statement: {},

            showIOSWarning: false,
            hasCopied: false,

            selectedPayAmount: "",
            progress: "amount"
        };

        //this.goBack = this.goBack.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        this.setStateAsync = this.setStateAsync.bind(this);
        this.submitPayment = this.submitPayment.bind(this);
        this.progressContinueMethod = this.progressContinueMethod.bind(this);
        this.progressGoBackAmount = this.progressGoBackAmount.bind(this);
    }

    public async componentDidMount() {
        document.title = "UOG - Pay Your Bill Online";

        if (this.isIOSNotSafari()) {
            // Warn user of incompatibility with non-safari browsers on iOS
            this.setState({
                showIOSWarning: true
            });
        }

        await this.loadStatement();
    }

    public componentWillUnmount() {
        window.removeEventListener("beforeunload", this.handleBeforeUnload);

        this.dropin?.teardown();
    }

    public render() {
        let content;
        if (this.state.globalLoading === true) {
            content = <div className="form-root">
                <h1 className="no-top-margin">Pay Your Bill Online</h1>
                <p>
                    Please wait while we find your statement...
                </p>
            </div>;
        }
        else if (typeof this.state.globalLoading === "string") {
            content = <div className="form-root">
                <h1 className="no-top-margin">Couldn't load bill</h1>
                <p>
                    We're sorry, but we encountered a problem while looking for your statement:
                </p>
                <span className="error-box slim">{this.state.globalLoading}</span>
            </div>;
        }
        else if (this.state.isBillNotFound === true) {
            content = <div className="form-root">
                <h1 className="no-top-margin">Couldn't find bill</h1>
                <p>
                    Thank you for your interest in the Howard Center for Women's Health and Tifton Woman's Center!
                </p>
                <p>
                    However, we were unable to find your bill.  If you have already paid your bill, then there is nothing
                    more you need to do.
                </p>
                <p>
                    If you have not paid your bill or believe this is otherwise incorrect, please call our billing
                    department at <a href="tel:+12293913506">(229) 391-3506</a>.  Please have your account and statement
                    numbers ready.
                </p>
            </div>;
        }
        else {
            content = <form className="form-root" onSubmit={e => { e.preventDefault(); return false; }}>
                <Switch>
                    <Route>
                        <h1 className="no-top-margin">Pay Your Bill Online</h1>
                        <p>
                            Hello, {this.state.statement.patientName}.
                        </p>
                        <p>
                            Thank you for visiting United Obstetrics and Gynecology.  According to our records, the
                            amount owed below has been determined to be your personal responsibility.
                        </p>
                        <p className="extra-pad">
                            Statement #<code>{this.state.statement.statementNumber}</code><br />
                            <br />
                            You owe<br />
                            <span className="money-big">${(+this.state.statement.statementAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span><br />
                            Payment due by {new Date(this.state.statement.datePaymentDue).toLocaleDateString()}<br />
                            <small>Does something not seem right, or do you have questions?  Please call us at <a href="tel:+12293913506">(229) 391-3506</a></small>
                        </p>
                        {
                            this.state.progress === "amount"
                                ? <>
                                    <div className="amount-selector">
                                        <label htmlFor="amount-list">Choose an amount to pay today</label>
                                        <ol id="amount-list" className="amount-list">
                                            {this.determinePaymentAmounts().map(a =>
                                                <li key={a.value}><RadioButton id={`usd-${a.value}`} name="selectedPayAmount" value={a.value} currentValue={this.state.selectedPayAmount} onChange={this.formInputChanged}>{a.text}</RadioButton></li>
                                            )}
                                        </ol>
                                    </div>
                                    <p>
                                        <button type="button" className="button" onClick={this.progressContinueMethod} disabled={!this.state.selectedPayAmount}>Continue</button>
                                    </p>
                                </>
                                : <p className="extra-pad">
                                    You've chosen to pay<br />
                                    <span className="money-big">${(+this.state.selectedPayAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span><br />
                                    <button type="button" className="light-button" onClick={this.progressGoBackAmount}>Select a different amount</button>
                                </p>
                        }
                        {this.state.progress === "method"
                            ? <>
                                {this.state.isPaymentLoading === true
                                    ? <div />
                                    : typeof this.state.isPaymentLoading === "string"
                                        ? <div className="error-box slim">{this.state.isPaymentLoading}</div>
                                        : null}
                                {this.state.showIOSWarning
                                    ? <>
                                        <div className="warning-box">
                                            To pay with Apple Pay or Venmo, you must open this page in Safari.<br />
                                            <br />
                                            {!!navigator.clipboard?.writeText
                                                ? <a href={document.location.href} className="button slim btn-200px" target="_blank" rel="noopener noreferrer" onClick={e => {
                                                    e.preventDefault();
                                                    navigator.clipboard.writeText(document.location.href);
                                                    this.setState({
                                                        hasCopied: true
                                                    }, () => {
                                                        window.setTimeout(() => this.setState({ hasCopied: false }), 5000)
                                                    });
                                                }}>{this.state.hasCopied ? "Copied!" : "Copy link to clipboard"}</a>
                                                : <span></span>
                                            }
                                        </div>
                                        <br />
                                    </>
                                    : null}
                                {this.state.progress === "method"
                                    ? <div id="payment-container" />
                                    : null}
                                {this.state.isPaymentPending === true
                                    ? <div>Please wait...</div>
                                    : typeof this.state.isPaymentPending === "string"
                                        ? <div className="error-box slim">{this.state.isPaymentPending}</div>
                                        : null
                                }
                                {this.state.isPaymentLoading === false
                                    ? <>
                                        <div className="input-group">
                                            <span className="checkbox">
                                                <input type="checkbox" id="agree-checkbox"
                                                    name="paymentAgree" checked={this.state.paymentAgree}
                                                    onChange={this.formInputChanged} />
                                                <label htmlFor="agree-checkbox">
                                                    By checking this box, you confirm that you <b><em>agree to pay {this.state.selectedPayAmount ? `$${this.state.selectedPayAmount}` : "the amount selected above"}</em></b>, and have information related
                                            to this transaction communicated to your selected payment provider for the purpose of processing
                                            the transaction.
                                        </label>
                                            </span>
                                        </div>
                                        <p>
                                            <button type="button" className="button" onClick={this.submitPayment} disabled={!this.state.isPaymentRequestable || !this.state.paymentAgree}>Pay</button>
                                        </p>
                                    </>
                                    : null}
                            </>
                            : null}
                    </Route>
                </Switch>
            </form>;
        }

        return <>
            <header>
                <img src={HCWHLogo} alt="Howard Center Logo" />
                <img src={TWCLogo} alt="Tifton Woman's Center Logo" />
            </header>
            {content}
            <footer className="footer-version">
                <small>&copy; {COPY_YEAR} United Obstetrics and Gynecology</small>
                <small> &mdash; <a href="http://www.thehowardcenter.com/Polterm/UOG%20Terms%20And%20Conditions%20Paperless%20Statements.pdf" target="_blank" rel="noopener noreferrer">Terms</a> &mdash; <a href="http://www.thehowardcenter.com/Polterm/UOG%20Financial%20Privacy%20Policy.pdf" target="_blank" rel="noopener noreferrer">Privacy</a></small>
                <small> &mdash; Payment services provided through <a href="https://braintreepayments.com" target="_blank" rel="noopener noreferrer">Braintree</a></small>
            </footer>
        </>;
    }

    private handleBeforeUnload(e: Event) {
        if (true/*!this.state.hasBegun*/) {
            return;
        }

        //e.preventDefault();
        //e.returnValue = true;

        //return true;
    }

    //private goBack() {
    //    let count = 1;
    //    if (this.props.history.location.state?.backSkipCount) {
    //        count = this.props.history.location.state?.backSkipCount;
    //    }

    //    this.props.history.go(-count);
    //}

    private formInputChanged(event: ChangeEvent<HTMLInputElement>) {
        const name = event.currentTarget.name;
        const value = event.currentTarget.type === "checkbox" ? event.currentTarget.checked : event.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private async setStateAsync<K extends keyof S, S = IPaymentRootState, P = RouteComponentProps<any>>(
        state: ((prevState: Readonly<S>, props: Readonly<P>) => (Pick<S, K> | S | null)) | (Pick<S, K> | S | null),
        callback?: () => void
    ): Promise<void> {
        if (callback) {
            return new Promise((resolve, reject) => {
                this.setState(state as any, () => {
                    resolve();
                    callback?.();
                });
            });
        }
        else {
            return new Promise((resolve, reject) => {
                this.setState(state as any, resolve);
            });
        }
    }

    private async loadStatement() {
        try {
            const statement = await V1.get<any>(`statement/${this.props.match.params.statement_id}`);

            this.setState({
                globalLoading: false,
                statement
            });

            return true;
        }
        catch (e) {
            if (e.statusCode === 404) {
                this.setState({
                    globalLoading: false,
                    isBillNotFound: true
                });
                return;
            }

            this.setState({
                globalLoading: e.message || "Could not load statement"
            });
            return false;
        }
    }

    private async initBraintree() {
        if (!this.state.statement.clientToken) {
            this.setState({
                isPaymentLoading: "Could not load payment options due to an invalid response from the server"
            });
            return;
        }

        const amount = this.state.selectedPayAmount;

        try {
            const dropin = await BrainTreeDropin.create({
                authorization: this.state.statement.clientToken,
                container: "#payment-container",
                card: {
                    overrides: {},
                    cardholderName: {
                        required: false,
                    }
                },
                dataCollector: {
                    paypal: true
                },
                paypal: {
                    flow: "checkout",
                    amount: amount,
                    currency: "USD"
                },
                venmo: {
                    allowNewBrowserTab: false
                },
                applePay: {
                    displayName: "United Obstetrics and Gynecology",
                    paymentRequest: {
                        total: {
                            amount: amount,
                            label: "United Obstetrics and Gynecology",
                        },
                        requiredBillingContactFields: ["postalAddress"]
                    }
                },
                googlePay: {
                    merchantId: "BCR2DN6T3OM2VDBQ",
                    transactionInfo: {
                        currencyCode: "USD",
                        totalPriceStatus: "FINAL",
                        totalPrice: amount
                    },
                    //cardRequirements: {
                    //    billingAddressRequired: true
                    //}
                },

            });

            this.setState({
                isPaymentLoading: false
            });


            this.dropin = dropin;

            dropin.on("paymentMethodRequestable", payload => {
                this.setState({
                    isPaymentRequestable: true
                });
            });

            dropin.on("noPaymentMethodRequestable", () => {
                this.setState({
                    isPaymentRequestable: false
                });
            });
        }
        catch (e) {
            this.setState({
                isPaymentLoading: "Could not load payment options"
            });
        }
    }

    private async submitPayment() {
        if (!this.dropin) {
            return;
        }

        try {
            this.setState({
                isPaymentPending: true
            });

            const paymentMethod = await this.dropin.requestPaymentMethod();

            const paymentData = {
                methodNonce: paymentMethod.nonce,
                deviceData: paymentMethod.deviceData,
                paymentAmount: this.state.selectedPayAmount,
            }

            await V1.post(`statement/${this.props.match.params.statement_id}/payment`, paymentData);

            this.setState({
                isPaymentPending: false
            });

            this.props.history.replace("/payment-success");
        }
        catch (e) {
            this.setState({
                isPaymentPending: e.message || "Could not process your payment at this time.  Please try again later."
            });
            // this.dropin?.clearSelectedPaymentMethod();
        }
    }

    private isIOSNotSafari() {
        const ua = window.navigator.userAgent;
        const iOS = !!ua.match(/iP(ad|hone)/i);
        const webkit = !!ua.match(/WebKit/i);
        const iOSSafari = iOS && webkit && !ua.match(/CriOS/i) && !ua.match(/OPiOS/i);

        return iOS && !iOSSafari;
    }

    private determinePaymentAmounts() {
        const amount = +this.state.statement.statementAmount;
        const options = [];

        if (amount > 20.00) {
            options.push({
                text: "$20.00",
                value: "20.00"
            });
        }
        if (amount > 50.00) {
            options.push({
                text: "$50.00",
                value: "50.00"
            });
        }
        if (amount > 100.00) {
            options.push({
                text: "$100.00",
                value: "100.00"
            });
        }

        // Percentage amounts
        if (amount * 0.25 > 100.00) {
            options.push({
                text: `$${(amount * 0.25).toFixed(2)}`,
                value: (amount * 0.25).toFixed(2)
            });
        }
        if (amount * 0.50 > 100.00) {
            options.push({
                text: `$${(amount * 0.50).toFixed(2)}`,
                value: (amount * 0.50).toFixed(2)
            });
        }

        options.push({
            text: `$${this.state.statement.statementAmount}`,
            value: this.state.statement.statementAmount
        });

        return options;
    }

    private progressContinueMethod() {
        this.setState({
            progress: "method"
        }, () => this.initBraintree());
    }

    private progressGoBackAmount() {
        this.setState({
            progress: "amount",
            isPaymentPending: false,
        });
    }
}

interface IRadioButtonProps {
    id: string;
    name: string;
    value: string;
    currentValue: string;
    children: ReactChild;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const RadioButton: React.FunctionComponent<IRadioButtonProps> = function (props: IRadioButtonProps) {
    return <>
        <input type="radio" id={props.id} name={props.name} value={props.value} checked={props.currentValue === props.value} onChange={props.onChange} tabIndex={0} />
        <label htmlFor={props.id}>{props.children}</label>
    </>;
}

export function PaymentSuccess() {
    return <>
        <header>
            <img src={HCWHLogo} alt="Howard Center Logo" />
            <img src={TWCLogo} alt="Tifton Woman's Center Logo" />
        </header>
        <div className="form-root">
            <h1 className="no-top-margin">Payment Accepted</h1>
            <p>
                You're all done!  Your payment has been accepted and is being processed.<br />
                Thank you for being a United Obstetrics and Gynecology patient!
            </p>
            <p>
                You can now close this page.
            </p>
        </div>
        <footer className="footer-version">
            <small>&copy; {COPY_YEAR} United Obstetrics and Gynecology</small>
            <small> &mdash; Payment services provided through <a href="https://braintreepayments.com" target="_blank" rel="noopener noreferrer">Braintree</a></small>
        </footer>
    </>;
}

export default PaymentRoot;
